import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  AppRemixIcon,
  AppSingleSelect,
  AppMultiSelect,
} from '@/layout/AppComponents';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import colors from '../../../../../constants/colors';
import ExpandedContainer from './ExpandedTabs/ExpandedContainer';
import EvaluationSidebarElement from './ExpandedTabs/EvaluationSidebarElement';
import * as currentPageAction from '@/store/actions/currentPageAction';
import { getPeriodicity } from '../tools/environmentalAspectsAssessmentTools';

export default function EnvironmentalAspectsAssessmentInspectionCard({ id }) {
  const dispatch = useDispatch();
  const inspections = useSelector((state) => state.currentPage?.inspections);
  const inspection = useSelector((state) =>
    state.currentPage?.inspections.find((i) => i?.inspectionID === id),
  );
  const checkedInspections = useSelector((state) => state.currentPage?.checkedCards);
  const periodicityOptions = useSelector(
    (state) => state.currentPage?.periodicityOptions,
  );
  const responsibleOptions = useSelector(
    (state) => state.currentPage?.responsibleOptions,
  );
  const [isOpened, setIsOpened] = useState(false);

  const handleCollapse = useCallback(() => {
    if (checkedInspections?.includes(id)) setIsOpened(!isOpened);
  }, [checkedInspections, id, isOpened]);

  const handlePeriodicity = useCallback(
    (v) => {
      const updatedInspections = inspections.map((i) => {
        if (i.inspectionID !== id) return i;

        return {
          ...i,
          periodicity: v,
        };
      });

      dispatch(currentPageAction.change('inspections', updatedInspections, true));
    },
    [inspections, id],
  );

  const handleResponsibles = useCallback(
    (v) => {
      const updatedInspections = inspections.map((i) => {
        if (i.inspectionID !== id) return i;

        return {
          ...i,
          responsibles: v,
        };
      });

      dispatch(currentPageAction.change('inspections', updatedInspections, true));
    },
    [inspections, id],
  );

  useEffect(() => {
    if (!checkedInspections?.includes(id)) setIsOpened(false);
  }, [checkedInspections]);

  return (
    <Container
      className="card mb-2"
      bodyClassName="p-1"
      $apply={checkedInspections?.includes(id)}
      $isCreatedForAssessment={inspection?.isCreatedForAssessment}
    >
      <div className="card">
        <div className="info-block">
          <div className="first-column line">
            <div>
              {inspection?.isCreatedForAssessment && (
                <AppRemixIcon
                  icon="user"
                  filled
                  title="Inspección personalizada"
                  color={colors.success}
                  s
                />
              )}
              <b>Inspección:</b>
            </div>
            <div>{inspection?.description}</div>
          </div>
          <div className="second-column">
            <div className="code line mb-1 mt-2">
              <div className="mr-1">
                <b>Código:</b>
              </div>
              <div>{inspection?.code}</div>
            </div>
            <div className="line mb-2">
              <AppSingleSelect
                title="Periodicidad"
                value={inspection?.periodicity}
                options={periodicityOptions}
                isDisabled={!checkedInspections?.includes(id)}
                onChange={handlePeriodicity}
                isLoading={!periodicityOptions}
                isClearable
              />
            </div>
            <div className="line mb-2">
              <AppMultiSelect
                title="Responsables"
                value={inspection?.responsibles}
                options={responsibleOptions}
                isDisabled={!checkedInspections?.includes(id)}
                onChange={handleResponsibles}
                isLoading={!responsibleOptions}
              />
            </div>
          </div>
        </div>
        <div className="info-block">
          <div className="first-column line">
            <div>
              <b>Última evaluación</b>
            </div>
            {inspection?.latestEvaluation ? (
              <ScrollBlock height="115px">
                <div className="evaluation">
                  <EvaluationSidebarElement
                    evaluation={inspection?.latestEvaluation}
                  />
                </div>
              </ScrollBlock>
            ) : (
              <div>No existe ninguna evaluación</div>
            )}
          </div>
        </div>
        <div
          className={`collapse-btn bg-primary ${
            checkedInspections?.includes(id) ? 'pointer' : 'not-allowed'
          } `}
          onClick={handleCollapse}
        >
          <AppRemixIcon
            icon={
              !checkedInspections?.includes(id)
                ? 'prohibited'
                : isOpened
                  ? 'subtract'
                  : 'add'
            }
            color="white"
          />
        </div>
      </div>
      <UnmountClosed isOpened={isOpened}>
        <ExpandedContainer inspectionID={id} />
      </UnmountClosed>
    </Container>
  );
}

const Container = styled.div`
  .card {
    display: grid;
    grid-template-columns: 1fr 250px 40px;
    border: 1px solid
      ${(props) => (props.$isCreatedForAssessment ? colors.success : colors.grey200)};

    & > .collapse-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 2px;
      font-size: 30px;
      padding: 0;
      height: 154px;
      &.not-allowed {
        opacity: 0.5;
      }
    }
    .info-block {
      background-color: ${(props) => (props.$apply ? '#fff' : colors.grey200)};
      display: flex;
      flex-direction: column;
      padding: 0 0.5rem;
      .first-column {
        flex-grow: 1;
        padding: 5px;
      }
      .second-column {
        min-width: 250px;
        display: flex;
        flex-direction: column;
        .code {
          flex-direction: row;
          justify-content: left;
        }
      }
      .line {
        color: ${colors.primaryLight};
      }
    }
    .info-block {
      @media (min-width: 1300px) {
        flex-direction: row;
      }
      .line {
        flex: 1 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }
    .input-block {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    .evaluation {
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
  .nav-tabs {
    margin-top: 0.5rem !important;
  }
`;

const ScrollBlock = styled.div`
  height: ${(props) => props.height ?? '340px'};
  padding-right: 10px;
  overflow-x: auto;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
